import React, { useState } from 'react';
import {
  AppBar,
  Toolbar,
  Box,
  IconButton,
  Drawer,
  List,
  ListItem,
  ListItemText,
  useMediaQuery,
  useTheme,
  Button,
  Container,
  Badge
} from '@mui/material';
import MenuIcon from '@mui/icons-material/Menu';
import CloseIcon from '@mui/icons-material/Close';
import ShoppingCartIcon from '@mui/icons-material/ShoppingCart';
import maxWeighLogo from '../assets/MaxWeighLogo.webp';
import { useNavigate } from 'react-router-dom';
import { useCart } from '../Context/CartContext';

const Navbar = ({ productsRef, aboutUsRef, enquiryRef, clientsRef }) => {
  const [mobileOpen, setMobileOpen] = useState(false);
  const navigate = useNavigate();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));
  const { getCartCount } = useCart();

  const handleDrawerToggle = () => {
    setMobileOpen(!mobileOpen);
  };

  const handleScroll = (currentRef, pathToNavigate) => {
    if (currentRef == null || currentRef.current == null) {
      navigate(pathToNavigate);
    } else if (currentRef.current) {
      currentRef.current.scrollIntoView({ behavior: 'smooth' });
    }
    setMobileOpen(false); // Close drawer on navigation
  };

  const navItems = [
    { label: 'HOME', ref: null, path: '/' },
    { label: 'ENQUIRY', ref: enquiryRef, path: '/Enquiry' },
    { label: 'ABOUT US', ref: aboutUsRef, path: '/AboutUs' },
    { label: 'PRODUCTS', ref: productsRef, path: '/Products' },
    { label: 'OUR CLIENTS', ref: clientsRef, path: '/OurClients' },
  ];

  const drawerContent = (
    <Box sx={{ width: 250, height: '100%', backgroundColor: '#7C001F', color: 'white' }}>
      <Box sx={{ display: 'flex', justifyContent: 'flex-end', p: 2 }}>
        <IconButton onClick={handleDrawerToggle} sx={{ color: 'white' }}>
          <CloseIcon />
        </IconButton>
      </Box>
      <List>
        {navItems.map((item) => (
          <ListItem
            button
            key={item.label}
            onClick={() => handleScroll(item.ref, item.path)}
            sx={{
              '&:hover': {
                backgroundColor: 'rgba(255, 255, 255, 0.1)',
              }
            }}
          >
            <ListItemText
              primary={item.label}
              primaryTypographyProps={{
                fontFamily: 'Inter, sans-serif',
                fontWeight: 600,
                textAlign: 'center'
              }}
            />
          </ListItem>
        ))}
        <ListItem
          button
          onClick={() => {
            navigate('/Cart');
            setMobileOpen(false);
          }}
          sx={{
            '&:hover': {
              backgroundColor: 'rgba(255, 255, 255, 0.1)',
            }
          }}
        >
          <ListItemText
            primary={`CART (${getCartCount()})`}
            primaryTypographyProps={{
              fontFamily: 'Inter, sans-serif',
              fontWeight: 600,
              textAlign: 'center'
            }}
          />
        </ListItem>
      </List>
    </Box>
  );

  return (
    <AppBar
      position="sticky"
      sx={{
        backgroundColor: 'rgba(124, 0, 31, 0.85)', // Semi-transparent Burgundy
        backdropFilter: 'blur(12px)', // Glassy effect
        boxShadow: '0 4px 30px rgba(0, 0, 0, 0.1)',
        borderBottom: '1px solid rgba(255, 255, 255, 0.1)',
      }}
    >
      <Container maxWidth="xl">
        <Toolbar disableGutters sx={{ justifyContent: 'space-between', height: '80px' }}>
          {/* Logo */}
          <Box
            component="img"
            src={maxWeighLogo}
            alt="MaxWeigh Logo"
            sx={{
              height: { xs: '40px', md: '50px' },
              width: 'auto',
              cursor: 'pointer'
            }}
            onClick={() => handleScroll(null, "/")}
          />

          {/* Desktop Navigation */}
          {!isMobile ? (
            <Box sx={{ display: 'flex', gap: 4, alignItems: 'center' }}>
              {navItems.map((item) => (
                <Button
                  key={item.label}
                  onClick={(e) => {
                    e.preventDefault();
                    handleScroll(item.ref, item.path);
                  }}
                  sx={{
                    color: '#FFF',
                    fontFamily: 'Inter, sans-serif',
                    fontSize: '14px',
                    fontWeight: 700,
                    textTransform: 'uppercase',
                    position: 'relative',
                    '&::after': {
                      content: '""',
                      position: 'absolute',
                      width: '0%',
                      height: '2px',
                      bottom: 0,
                      left: '50%',
                      transform: 'translateX(-50%)',
                      backgroundColor: '#fff',
                      transition: 'width 0.3s ease-in-out'
                    },
                    '&:hover::after': {
                      width: '80%'
                    }
                  }}
                >
                  {item.label}
                </Button>
              ))}
              <IconButton color="inherit" onClick={() => navigate('/Cart')}>
                <Badge badgeContent={getCartCount()} color="secondary">
                  <ShoppingCartIcon />
                </Badge>
              </IconButton>
            </Box>
          ) : (
            /* Mobile Menu Icon */
            <Box sx={{ display: 'flex', alignItems: 'center' }}>
              <IconButton color="inherit" onClick={() => navigate('/Cart')} sx={{ mr: 1 }}>
                <Badge badgeContent={getCartCount()} color="secondary">
                  <ShoppingCartIcon />
                </Badge>
              </IconButton>
              <IconButton
                color="inherit"
                aria-label="open drawer"
                edge="start"
                onClick={handleDrawerToggle}
              >
                <MenuIcon />
              </IconButton>
            </Box>
          )}
        </Toolbar>
      </Container>

      {/* Mobile Drawer */}
      <Drawer
        anchor="right"
        open={mobileOpen}
        onClose={handleDrawerToggle}
        ModalProps={{
          keepMounted: true, // Better open performance on mobile.
        }}
      >
        {drawerContent}
      </Drawer>
    </AppBar>
  );
};

export default Navbar;
